import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Drawer,
  FailedCallout,
  FieldHint,
  FieldLabel,
  ReasonField,
  Select,
  TextField,
  TextInput,
  errorText,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import {
  QUOTA_GROUPS,
  QUOTA_GROUP_LABELS,
  SOURCE_KINDS,
  SOURCE_KIND_EDIT_LABELS,
  rebaseSourceDraft,
  sourceEditChanges,
  type SourceEditFields,
  type SourceEditPerson,
  type SourceEditView,
} from "../../contracts/index";
import { AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT, profileOptionsQuery, sourceCommands, sourceEditQuery } from "../queries";
import {
  BreaksField,
  ChangesBox,
  ConflictCallout,
  TierPreview,
  breakProblem,
  sourceIsPlanless,
} from "./EditSourceParts";
import { PersonCard, PersonOptions, TierField, personOptions } from "./SourceFormParts";

type FieldKey = Exclude<keyof SourceEditFields, "people">;
const FIELD_KEYS: readonly FieldKey[] = ["title", "kind", "cadence_tier", "known_breaks", "quota_group", "owner_team"];

function undoChange(draft: SourceEditFields, base: SourceEditFields, key: string): SourceEditFields {
  if (key.startsWith("person:")) {
    const id = key.slice("person:".length);
    const original = base.people.find((p) => p.profile_id === id);
    const others = draft.people.filter((p) => p.profile_id !== id);
    return { ...draft, people: original ? [...others, original] : others };
  }
  const restore: Record<FieldKey, SourceEditFields> = {
    title: { ...draft, title: base.title },
    kind: { ...draft, kind: base.kind },
    cadence_tier: { ...draft, cadence_tier: base.cadence_tier },
    known_breaks: { ...draft, known_breaks: base.known_breaks },
    quota_group: { ...draft, quota_group: base.quota_group },
    owner_team: { ...draft, owner_team: base.owner_team },
  };
  const field = FIELD_KEYS.find((k) => k === key);
  return field ? restore[field] : draft;
}

export function EditSourcePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const query = useQuery(sourceEditQuery(id));
  if (!query.data) {
    return (
      <Drawer title={query.isError ? "Source not found" : "Loading source…"} onClose={onClose}>
        {query.isError ? <p className="m-0 text-muted">{errorText(query.error)}</p> : null}
      </Drawer>
    );
  }
  return <EditSourceForm opened={query.data} latest={query.data} fetching={query.isFetching} onClose={onClose} />;
}

function PeopleField({
  view,
  draft,
  nameOf,
  onChange,
}: {
  view: SourceEditView;
  draft: SourceEditFields;
  nameOf: (id: string) => string;
  onChange: (people: SourceEditPerson[]) => void;
}) {
  const profiles = useQuery(profileOptionsQuery);
  const [find, setFind] = useState("");
  const options = useMemo(
    () => personOptions(profiles.data?.profiles, find, draft.people),
    [profiles.data, find, draft.people],
  );
  const update = (pid: string, patch: Partial<SourceEditPerson>) => {
    onChange(draft.people.map((p) => (p.profile_id === pid ? { ...p, ...patch } : p)));
  };
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend className="contents">
        <FieldLabel optional="optional">People</FieldLabel>
      </legend>
      {draft.people.map((p) => (
        <PersonCard
          key={p.profile_id}
          entry={{ profile_id: p.profile_id, relationship: p.relationship, inPlan: p.in_plan, required: p.required }}
          name={nameOf(p.profile_id)}
          planLabel="In their plan"
          onUpdate={(patch) => {
            update(p.profile_id, {
              ...(patch.relationship !== undefined ? { relationship: patch.relationship } : {}),
              ...(patch.inPlan !== undefined ? { in_plan: patch.inPlan } : {}),
              ...(patch.required !== undefined ? { required: patch.required } : {}),
            });
          }}
          onRemove={() => {
            onChange(draft.people.filter((x) => x.profile_id !== p.profile_id));
          }}
        />
      ))}
      <label className="sr-only" htmlFor="edit-source-find">
        Find a person
      </label>
      <TextInput
        id="edit-source-find"
        type="search"
        placeholder="Add a person"
        value={find}
        onChange={(e) => {
          setFind(e.target.value);
        }}
      />
      <PersonOptions
        options={options}
        onPick={(pid) => {
          onChange([...draft.people, { profile_id: pid, relationship: "own", in_plan: true, required: false }]);
          setFind("");
        }}
      />
      <FieldHint>
        {view.collected
          ? "Plan changes show Changes pending on each profile until the scheduler applies them."
          : `${view.platform_label} isn't collected yet: nothing is scanned, whatever the plans say.`}
      </FieldHint>
    </fieldset>
  );
}

function KindAndQuota({ draft, set }: { draft: SourceEditFields; set: (patch: Partial<SourceEditFields>) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-compact:grid-cols-1">
      <div className="mt-3">
        <FieldLabel htmlFor="edit-source-kind">Kind</FieldLabel>
        <Select
          id="edit-source-kind"
          value={draft.kind}
          onChange={(e) => {
            set({ kind: SOURCE_KINDS.find((k) => k === e.target.value) ?? draft.kind });
          }}
        >
          {SOURCE_KINDS.map((k) => (
            <option key={k} value={k}>
              {SOURCE_KIND_EDIT_LABELS[k]}
            </option>
          ))}
        </Select>
      </div>
      <div className="mt-3">
        <FieldLabel htmlFor="edit-source-quota">Quota group</FieldLabel>
        <Select
          id="edit-source-quota"
          value={draft.quota_group ?? ""}
          onChange={(e) => {
            set({ quota_group: QUOTA_GROUPS.find((q) => q === e.target.value) ?? null });
          }}
        >
          <option value="">Platform default</option>
          {QUOTA_GROUPS.map((q) => (
            <option key={q} value={q}>
              {QUOTA_GROUP_LABELS[q]}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

function EditFooter({ pending, failed, onClose }: { pending: boolean; failed: boolean; onClose: () => void }) {
  return (
    <span className="flex items-center justify-end gap-2">
      <span className="mr-auto text-md text-muted max-compact:hidden">
        Changes are listed in the source&apos;s History and the audit log.
      </span>
      <Button size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button size="sm" variant="primary" type="submit" form="edit-source-form" aria-busy={pending || undefined}>
        {failed ? "Try again" : "Save changes"}
      </Button>
    </span>
  );
}

function formErrors(draft: SourceEditFields, changes: number, server: Record<string, string>) {
  return {
    title: server["title"] ?? (draft.title.trim() ? null : "Give the source a name people recognise"),
    breaks: breakProblem(draft.known_breaks) ?? server["known_breaks"] ?? null,
    nothing: changes === 0 ? "Nothing changed yet" : null,
  };
}

function EditSourceForm({
  opened,
  latest,
  fetching,
  onClose,
}: {
  opened: SourceEditView;
  latest: SourceEditView;
  fetching: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const profiles = useQuery(profileOptionsQuery);
  const [base, setBase] = useState(opened);
  const [draft, setDraft] = useState(opened.fields);
  const [submitted, setSubmitted] = useState(false);
  const set = (patch: Partial<SourceEditFields>) => {
    setDraft({ ...draft, ...patch });
  };
  const nameOf = (pid: string) =>
    latest.names[pid] ?? base.names[pid] ?? profiles.data?.profiles.find((p) => p.id === pid)?.name ?? "Unknown person";
  const changes = sourceEditChanges(base.fields, draft, nameOf);
  const mutation = useCommandMutation({
    run: (csrf, reason: string) =>
      sourceCommands.edit(
        base.id,
        { ...draft, base_revision: base.revision, base_plans: base.plans_version, reason },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: (result) => {
      toast.show(result.plans_pending > 0 ? "Source saved · plans show Changes pending" : "Source saved");
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict" ? mutation.error : null;
  const server = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const r = useReason(server["reason"]);
  const newer = latest.revision !== base.revision || latest.plans_version !== base.plans_version;
  const rebase = (fields: SourceEditFields) => {
    setDraft(fields);
    setBase(latest);
    mutation.reset();
  };
  const errors = formErrors(draft, changes.length, server);
  return (
    <Drawer
      title="Edit source"
      subtitle={`${base.fields.title} · ${base.platform_label}`}
      onClose={onClose}
      footer={<EditFooter pending={mutation.isPending} failed={mutation.isError && !conflict} onClose={onClose} />}
    >
      <form
        id="edit-source-form"
        noValidate
        className="text-ui [&>div:first-child]:mt-0"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          const reasonOk = r.submit();
          if (!reasonOk || Object.values(formErrors(draft, changes.length, {})).some(Boolean)) return;
          mutation.mutate(r.trimmed);
        }}
      >
        <TextField
          label="Name"
          required
          value={draft.title}
          error={submitted ? errors.title : null}
          onChange={(e) => {
            set({ title: e.target.value });
          }}
        />
        <KindAndQuota draft={draft} set={set} />
        <PeopleField
          view={base}
          draft={draft}
          nameOf={nameOf}
          onChange={(people) => {
            set({ people });
          }}
        />
        <TierField
          id="edit-source-tier"
          value={draft.cadence_tier}
          onChange={(cadence_tier) => {
            set({ cadence_tier });
          }}
          preview={
            <TierPreview
              before={base.fields}
              after={draft}
              youtube={
                draft.quota_group === "youtube_data_api" || (draft.quota_group === null && base.platform === "youtube")
              }
            />
          }
        />
        <BreaksField
          breaks={draft.known_breaks}
          error={submitted ? errors.breaks : null}
          onChange={(known_breaks) => {
            set({ known_breaks });
          }}
        />
        <TextField
          label="Owner team"
          optional="optional"
          placeholder="e.g. UK politics desk"
          value={draft.owner_team ?? ""}
          hint="For reference only. Gaps and problems go to Tasks, not to a team."
          onChange={(e) => {
            set({ owner_team: e.target.value || null });
          }}
        />
        <ChangesBox
          changes={changes}
          noPlan={sourceIsPlanless(base, draft) && !sourceIsPlanless(base, base.fields)}
          onUndo={(key) => {
            setDraft(undoChange(draft, base.fields, key));
          }}
        />
        {submitted && errors.nothing ? <p className="m-0 mt-1.5 text-md text-attention">{errors.nothing}</p> : null}
        <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
        {conflict ? (
          <ConflictCallout
            error={conflict}
            meanwhile={sourceEditChanges(base.fields, latest.fields, nameOf).map((c) => c.label)}
            ready={newer && !fetching}
            onKeep={() => {
              rebase(rebaseSourceDraft(base.fields, latest.fields, draft));
            }}
            onDiscard={() => {
              rebase(latest.fields);
            }}
          />
        ) : (
          <FailedCallout error={mutation.error} fieldsShown />
        )}
      </form>
    </Drawer>
  );
}
